import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/** Maximum animation duration in seconds — prevents ultra-wide screens feeling frozen. */
const MAX_SPEED_SECONDS = 60;

/**
 * Smoothstep easing — smooth acceleration and deceleration.
 * @param {number} t - Progress value between 0 and 1.
 * @returns {number}
 */
function smoothstep(t) {
  return t * t * (3 - 2 * t);
}

/**
 * Animate a numeric property smoothly.
 * @param {Object} params
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration in milliseconds.
 * @param {function(number): void} params.onUpdate - Called on each animation frame.
 * @param {function(number): number} [params.easing] - Easing function (defaults to smoothstep).
 * @param {function(): void} [params.onComplete] - Called when animation completes.
 * @returns {{ current: number, cancel: () => void }}
 */
function animateValue({ from, to, duration, onUpdate, easing = smoothstep, onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    currentValue = from + (to - from) * easing(progress);

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

/**
 * A custom element that displays a marquee.
 *
 * Copies are pre-rendered in Liquid (8 total across 2 halves), so JS only
 * needs to handle hover slow-down/speed-up. The CSS animation translates by
 * -50% for a seamless loop.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  connectedCallback() {
    super.connectedCallback();

    // Respect reduced-motion preference — skip entirely
    if (this.#reducedMotion.matches) return;

    // Listen for OS-level reduced-motion changes mid-session
    this.#reducedMotion.addEventListener('change', this.#handleReducedMotionChange);

    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#reducedMotion.removeEventListener('change', this.#handleReducedMotionChange);

    // Cancel any in-progress playback-rate animation
    this.#animation?.cancel();
    this.#animation = null;

    // Cancel any pending debounced callbacks
    this.#slowDown.cancel();
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /** MediaQueryList for prefers-reduced-motion */
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  #handleReducedMotionChange = () => {
    if (this.#reducedMotion.matches) {
      const animations = this.refs.wrapper.getAnimations();
      for (const animation of animations) {
        animation.cancel();
      }
      this.#animation?.cancel();
      this.#animation = null;
      this.removeEventListener('pointerenter', this.#slowDown);
      this.removeEventListener('pointerleave', this.#speedUp);
    } else {
      this.connectedCallback();
    }
  };

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp = () => {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
